/**
 * PreviewAsBanner — yellow sticky banner shown when admin is impersonating
 * another user (preview mode).  Exposes an "Exit Preview" button.
 *
 * PreviewAsPicker — modal that searches active users and lets admin start
 * a preview session.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import useSearchInput from "@/hooks/useSearchInput";
import { toast } from "sonner";
import { Eye, X, Search, User as UserIcon, Briefcase, UserCheck, ArrowRightLeft, Users2 } from "lucide-react";

export function PreviewAsBanner() {
    const { user, impersonatedBy, stopPreviewAs } = useAuth();
    const navigate = useNavigate();
    if (!impersonatedBy || !user) return null;
    const isFamily = impersonatedBy.kind === "family";
    const needsPwdChange = user.must_change_password;
    const exit = async () => {
        try {
            await stopPreviewAs();
            toast.success(isFamily ? "Back to your own account" : "Returned to admin view");
            navigate(isFamily ? "/portal" : "/");
        } catch (e) {
            toast.error(formatError(e));
        }
    };
    // Family-switch variant: amber→rose gradient, "Switch back" CTA.
    if (isFamily) {
        return (
            <div
                data-testid="family-switch-banner"
                className="sticky top-0 z-50 bg-gradient-to-r from-[#F59E0B] to-[#DC2626] text-white px-4 py-2 shadow-md border-b-2 border-[#7F1D1D]"
            >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-sm font-bold">
                        <Users2 size={16} />
                        <span>
                            Viewing as <strong className="text-amber-100">{user.trade_name || user.name || user.identifier}</strong>
                            {impersonatedBy.family_name && (
                                <span className="text-[11px] italic opacity-90 ml-1">· via family &ldquo;{impersonatedBy.family_name}&rdquo;</span>
                            )}
                            <span className="text-[11px] opacity-75 ml-1">— actions you take are recorded against THIS account.</span>
                        </span>
                    </div>
                    <button
                        onClick={exit}
                        data-testid="family-switch-back"
                        className="bg-white hover:bg-amber-50 text-[#7F1D1D] text-xs font-bold px-3 py-1 rounded uppercase tracking-wider flex items-center gap-1"
                    >
                        <ArrowRightLeft size={12} /> Switch back
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div
            data-testid="preview-as-banner"
            className="sticky top-0 z-50 bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-[#0A1128] px-4 py-2 shadow-md border-b-2 border-[#92400E]"
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-bold">
                    <Eye size={16} />
                    <span>👁️ Previewing as <strong>{user.name || user.identifier}</strong> ({user.role}) — you are NOT logged in as them, just viewing their UI.</span>
                    <span className="text-[10px] opacity-70 italic ml-1">started by {impersonatedBy.name}</span>
                </div>
                <button
                    onClick={exit}
                    data-testid="preview-as-exit"
                    className="bg-[#0A1128] hover:bg-[#1a2440] text-[#C5A059] text-xs font-bold px-3 py-1 rounded uppercase tracking-wider flex items-center gap-1"
                >
                    <X size={12} /> Exit Preview
                </button>
            </div>
            {needsPwdChange && (
                <div className="text-[11px] mt-1 italic flex items-center gap-1 opacity-90">
                    ⚠️ This user hasn&apos;t logged in yet — the change-password gate is bypassed for preview. Once they actually log in &amp; set a password, they will land directly on the dashboard.
                </div>
            )}
        </div>
    );
}

const ROLE_TABS = [
    { id: "client", label: "Clients", icon: UserIcon },
    { id: "staff", label: "Staff", icon: UserCheck },
    { id: "vendor", label: "Vendors", icon: Briefcase },
];

export function PreviewAsPicker({ open, onClose }) {
    const { startPreviewAs } = useAuth();
    const navigate = useNavigate();
    const [role, setRole] = useState("client");
    const { q, setQ, activeQ, hint: searchHint } = useSearchInput("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState("");

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get("/auth/impersonatable-users", {
                    params: { role, q: activeQ },
                });
                if (!cancelled) setUsers(data);
            } catch (e) {
                if (!cancelled) toast.error(formatError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, role, activeQ]);

    const start = async (u) => {
        setStarting(u.id);
        try {
            await startPreviewAs(u.id);
            toast.success(`Now previewing as ${u.name || u.identifier}`);
            onClose();
            // Navigate to root so the impersonated user lands on their own dashboard
            navigate("/");
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setStarting("");
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="preview-as-modal">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <div className="font-heading text-lg font-bold text-[#0A1128] flex items-center gap-2">
                        <Eye size={20} className="text-[#F59E0B]" /> Preview as another user
                    </div>
                    <button onClick={onClose} data-testid="preview-as-close"><X size={20} /></button>
                </div>

                <div className="px-5 py-3 border-b">
                    <div className="flex gap-2 mb-3">
                        {ROLE_TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setRole(t.id)}
                                data-testid={`preview-as-role-${t.id}`}
                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold rounded uppercase tracking-wider ${role === t.id ? "bg-[#0A1128] text-white" : "bg-[#F4F4F5] text-[#71717A]"}`}
                            >
                                <t.icon size={12} />{t.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F4F4F5] rounded">
                        <Search size={14} className="text-[#71717A]" />
                        <input
                            data-testid="preview-as-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name, identifier, email… (3+ letters)"
                            className="flex-1 bg-transparent text-sm outline-none"
                        />
                    </div>
                    {searchHint && (
                        <div className="text-[11px] text-[#A1A1AA] pl-2 -mt-1" data-testid="preview-as-search-hint">{searchHint}</div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading && <div className="text-center py-8 text-sm text-[#71717A]">Loading…</div>}
                    {!loading && users.length === 0 && (
                        <div className="text-center py-8 text-sm text-[#71717A]">No active {role}s match.</div>
                    )}
                    {users.map((u) => (
                        <button
                            key={u.id}
                            onClick={() => start(u)}
                            disabled={starting !== ""}
                            data-testid={`preview-as-user-${u.id}`}
                            className="w-full text-left flex items-center justify-between px-5 py-3 hover:bg-[#FFFBEB] border-b border-[#F4F4F5] disabled:opacity-50"
                        >
                            <div>
                                <div className="text-sm font-medium text-[#0A1128]">{u.name || u.identifier}</div>
                                <div className="text-[11px] text-[#71717A] flex items-center gap-2">
                                    <span>{u.identifier}</span>
                                    {u.email && <><span>·</span><span>{u.email}</span></>}
                                </div>
                            </div>
                            <span className="text-[10px] uppercase tracking-wider font-bold bg-[#F4F4F5] text-[#71717A] px-2 py-0.5 rounded">
                                {starting === u.id ? "Starting…" : "Preview"}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="px-5 py-3 border-t bg-[#FFFBEB] text-xs text-[#92400E]">
                    💡 Preview session lasts 30 minutes. All previews are logged in the audit trail.
                </div>
            </div>
        </div>
    );
}
